import { api } from './api.js'

// The work beacon (§20.2). While an agent has a ticket open, this tells the server
// the ticket is in front of them — and stops telling it the moment it isn't.
//
// Two conditions gate every beat, because "a tab is open" is not "someone is working":
// the tab has to be VISIBLE, and the agent has to have touched something recently.
// A ticket left open on a second monitor over lunch goes quiet on its own, and the
// server's idle cutoff closes the session shortly after.
//
// The server is the one that decides what a beat is worth — it credits at most one
// interval per beat and clamps the rest (§20.2) — so this module is only ever
// reporting presence, never claiming time.

const INTERVAL_MS = 60_000        // must match WORKLOG_PING_SECONDS
const IDLE_MS = 180_000           // mirrors WORKLOG_IDLE_SECONDS
const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart']

export function startWorkBeacon(ticketNumber, onTotals) {
  let lastActivity = Date.now()
  let timer = null
  let stopped = false

  const touched = () => { lastActivity = Date.now() }
  const active = () => document.visibilityState === 'visible' && (Date.now() - lastActivity) < IDLE_MS

  const beat = async (body) => {
    if (stopped && !body?.closing) return
    try {
      const totals = await api.post(`/worklog/tickets/${ticketNumber}/ping`, body || {})
      if (totals && onTotals) onTotals(totals)
    } catch {
      // A dropped beat costs a minute of credit and nothing else, so it is not worth
      // a toast: the agent is trying to answer a ticket, not run a timer.
    }
  }

  for (const ev of ACTIVITY) window.addEventListener(ev, touched, { passive: true })
  // Coming back to the tab counts as arriving, so the first beat after it isn't
  // discarded as idle.
  document.addEventListener('visibilitychange', touched)

  beat()                                    // presence; the next beat credits the gap
  timer = setInterval(() => { if (active()) beat() }, INTERVAL_MS)

  return {
    close() {
      if (stopped) return
      stopped = true
      clearInterval(timer)
      for (const ev of ACTIVITY) window.removeEventListener(ev, touched)
      document.removeEventListener('visibilitychange', touched)
      // Best-effort: a hard tab close never gets here, which is why nothing depends
      // on it (§20.2 — the idle cutoff and the sweep both close a session anyway).
      beat({ closing: true })
    }
  }
}
